/*
 *                     NOTICE
 *            COPYRIGHT 2025 Ab Initio
 *       UNPUBLISHED - ALL RIGHTS RESERVED
 *
 * This program is confidential and a trade secret of Ab Initio.
 * The receipt or possession of this program does not convey any
 * rights to reproduce or disclose its contents, or to manufacture,
 * use or sell anything that it may describe, in whole or in part,
 * without the specific written consent of Ab Initio.
 */

import { BootstrapApp } from "abreact/incubator/ui/app/bootstrap/BootstrapApp";
import { ClassUtils } from "abweb/utils/ClassUtils";
import { LogAndRenderErrorPipeline } from "abweb/utils/error/LogAndRenderErrorPipeline";
import { LogUtils } from "abweb/utils/log/LogUtils";
import { NumberUtils } from "abweb/utils/NumberUtils";
import { ProfileUtils } from "abweb/utils/profile/ProfileUtils";
const LOG = LogUtils.getLogger(__filename);

/**
 * This is the generic react app loading code that uses webpack to load the application-specific
 * *AppLoader module for an application.  This function is directly invoked from index.html to load the
 * the application. The *AppLoader module must have a static startApp method with the following signature:
 * <pre>startApp(alProps: AppLoaderProps): Promise<() => React.ReactNode></pre>
 * It returns a method that will render the application.  Typically it will only return the render function once
 * the underlying application is fully initialized (including all service initialization).
 *
 * @param appLoaderFn startApp function from *AppLoader module.
 * @param containingNode  dom element where application will render
 * @param absdkDirPrefix The absdkDir is the directory that contains webapp and jspm_packages, which in turn contain
 * the ab initio libraries and third party libraries.
 * absdk apps use absdkDirPrefix to indicate relative path from their index.html location
 * to the absdk directory (typically "../absdk/"). Ab Initio products have the index.html installed in the
 * absdk directory, so they don't need a prefix (effectively an empty string)
 * @param headless true for a "headless" application that will not be rendering UI.
 * This disables the layer manager.
 * @param portalLoginView if true, will change the background when loading the app to match the login page
 */
export async function reactBootstrapLoader(appLoaderFn, containingNode, absdkDirPrefix = "", headless = false, portalLoginView = false) {
  try {
    myProfiler("Starting reactAppModuleLoader()");
    if (containingNode == null) {
      throw new Error("Application Containing Node was undefined");
    }
    absdkDirPrefix = adjustAbsdkDirPrefix(absdkDirPrefix);
    const errorPipeline = new LogAndRenderErrorPipeline();
    renderLoadingIndicator(containingNode, absdkDirPrefix);
    myProfiler("Spinner rendered");
    BootstrapApp.startApp(containingNode, appLoaderFn, errorPipeline, profileStartupFn, absdkDirPrefix, headless, portalLoginView);
    myProfiler("Bootstrap initialization finished");
  } catch (err) {
    // If an error is thrown that reaches all the way here (the app loader),
    // try really hard to give as much diagnostic info as possible...
    if (ClassUtils.isInstanceOf(err, Error)) {
      // Note this often gives more info (such as failing abinitio src file and line num) that is NOT available
      // on the default logging on the rethrown error below
      LOG.error("Raw application error - " + err.message + (err.stack ? "\n  Stack: " + err.stack : "")); // just in case some browsers don't have error.stack
    } else {
      LOG.error("Raw application error - " + err);
    }
    throw err; // better stack traces if rethrow
  }
}
function adjustAbsdkDirPrefix(absdkDirPrefix) {
  if (!absdkDirPrefix) {
    return "";
  }
  // ensure trailing slash
  return absdkDirPrefix.lastIndexOf("/") !== absdkDirPrefix.length - 1 ? absdkDirPrefix += "/" : absdkDirPrefix;
}
export function profileStartupFn(message) {
  const profileLabel = "Startup: " + message;
  const mark = performance.mark(profileLabel); // show in chrome debugger 'Performance' tab (look for 'Timings')
  const startTime = mark?.startTime ?? performance.now(); // MH-24813 as some FF versions (e.g., 91) return mark as undefined
  ProfileUtils.add(profileLabel, "ms since app start", startTime);
  const elapsedMSec = NumberUtils.FORMAT_DECIMAL_2(startTime); // thousands sep, 2 decimal places
  LOG.info("PROFILE: Startup " + message + " (ms since app start): " + elapsedMSec);
}
function myProfiler(msg) {
  profileStartupFn("ReactBootstrapLoader: " + msg);
}

// MUCH more maintainable across products then embedding the below html in every product-specific index*.html file
function renderLoadingIndicator(containingNode, absdkDirPrefix) {
  // Equivalent to react-based spinner rendered by BoostrapApp:
  //        <AbBusyIndicator size={AbBusyIndicatorSize.LARGE} message={this.state.loadingMsg}/>
  containingNode.innerHTML = `<div class="ab-busy-indicator ab-busy-indicator-large">
  <div class="ab-busy-indicator-image"><img src="${absdkDirPrefix}webapp/abweb/assets/images/spinner_double_delay.svg"></div>
</div>`;
}
//# sourceMappingURL=ReactBootstrapLoader.js.map
